import { TagBoxModel } from './TagBoxModel.js';

export class TagStore {
    #tags = new Map();
    #mediator = null;
    #docId = null;

    constructor(mediator = null, docId = null) {
        this.#mediator = mediator;
        this.#docId = docId;
    }

    #emit(event, payload) {
        this.#mediator?.publish(event, { ...payload, docId: this.#docId });
    }

    initFromApiResponse(apiDataArray) {
        this.clear();
        for (const data of apiDataArray) {
            const model = TagBoxModel.fromApiResponse(data);
            this.#tags.set(model.id, model);
        }
    }

    add(model) {
        this.#tags.set(model.id, model);
        this.#emit('tag:created', { tagId: model.id, model });
        return model;
    }

    load(model) {
        this.#tags.set(model.id, model);
        return model;
    }

    get(id) {
        return this.#tags.get(id) ?? null;
    }

    update(id, patch) {
        const model = this.#tags.get(id);
        if (!model) return null;
        const prev = { ...model };
        const updated = { ...model, ...patch };
        this.#tags.set(id, updated);
        this.#emit('tag:updated', { tagId: id, patch, prev, model: updated });
        return updated;
    }

    /**
     * Move a box to its real tag_id once the server has reported it (it was drawn under a negative placeholder,
     * or read from a document that had not been migrated yet). Silent on purpose: nothing about the box changed,
     * only how it is addressed — a tag:updated would make the core queue another save.
     */
    rekey(oldId, newId) {
        const model = this.#tags.get(oldId);
        if (!model || oldId === newId || this.#tags.has(newId)) { return null; }
        const moved = { ...model, id: newId };
        this.#tags.delete(oldId);
        this.#tags.set(newId, moved);
        return moved;
    }

    remove(id) {
        const model = this.#tags.get(id);
        const deleted = this.#tags.delete(id);
        if (deleted) this.#emit('tag:removed', { tagId: id, model });
        return deleted;
    }

    getAll() {
        return [...this.#tags.values()];
    }

    clear() {
        this.#tags.clear();
        this.#emit('tag:store:cleared', {});
    }

    count() {
        return this.#tags.size;
    }
}

